import { canTenantRole, type TenantRole } from "@loot-os/api-contract";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { useTRPC } from "./trpc";

/**
 * The signed-in user's standing: platform admin or not, their tenants, and their
 * role in the route's tenant. One `me.tenants` query backs every caller (nav gating,
 * the tenant picker, TenantLayout's name lookup), so the role is one cache read.
 *
 * `role` is null while loading or outside a tenant route. Platform admins act on
 * every tenant as admin (the server grants them owner via impersonation). UI
 * gating is a courtesy — the server enforces every role for real.
 */
export function useTenantRole() {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };
  const me = useQuery(trpc.me.tenants.queryOptions());

  const tenant = tenantId ? me.data?.tenants.find((o) => o.id === tenantId) : undefined;
  const role = tenant?.role ?? (me.data?.platformAdmin && tenantId ? "admin" : null);

  return {
    query: me,
    isPlatformAdmin: me.data?.platformAdmin ?? false,
    tenants: me.data?.tenants ?? [],
    /** Role in the route's tenant; null while loading or outside a tenant. */
    role,
    /** can("editor") / can("admin") — false while the role is still loading. */
    can: (min: TenantRole) => (role ? canTenantRole(role, min) : false),
  };
}
